import type { SubmissionService } from './types'
import type Plan from '@/classes/Plan'
import { DocumentType } from '@/enums/DocumentType'
import { PlanPaymentType } from '@/enums/PlanPaymentType'
import { useRequestQueue } from '@/mixins/requestQueue'
import { windowRequestQueueConfig } from '@/mixins/requestQueueWindowConfig'
import { Feature, useFeatureService } from '@/services/featureService'
import { useRetryMethods } from '@/services/retryUtils'
import { useAuthStore } from '@/stores/auth'
import { useDocumentStore } from '@/stores/document'
import { useSessionStore } from '@/stores/session'
import { useSessionDocumentsStore } from '@/stores/sessionDocuments'
import type {
	EFTPayment,
	MissingSignature,
	MissingSignaturesBySigner,
	SignerInfo,
} from '@/types'
import { faker } from '@faker-js/faker'
import { captureException } from '@sentry/vue'
import axios, { type AxiosRequestConfig } from 'axios'
import localforageLib from 'localforage'
import { DateTime } from 'luxon'

const localNglSubmissions = localforageLib.createInstance({
	name: 'request-queue',
	storeName: 'nglSubmissions',
})

const axiosInstance = axios.create({
	method: 'POST',
	withCredentials: true,
	headers: {
		'Content-Type': 'multipart/form-data',
	},
})

/**
 * This matches our payload to the field types
 * on the Request on the Middleware endpoint.
 * This type should only be used in this service.
 */
type NglSubmissionRequest = {
	sessionId: string
	planId: string
	document: File
	correlationId: string
	crmLeadId?: string | null
	crmAppointmentId?: string | null
	planholderName: string
	salesforceContactAfpId?: string
	signaturesRequired?: boolean
	signers?: { FuneralDirector: Omit<SignerInfo, 'id'> }
	signatures?: Record<any, MissingSignature[]>
}

// Add a request interceptor to dynamically set auth token
axiosInstance.interceptors.request.use(async (config) => {
	const authStore = useAuthStore()
	const token = await authStore.getAuthToken()
	if (token) {
		config.headers['X-PAPI-AUTHENTICATED-WITH'] = token
	}
	return config
})

const nglSubmissionsQueueConfig = {
	axiosInstance,
	requestBackgroundSync: async (tag: string) => {
		await windowRequestQueueConfig.requestBackgroundSync('submission-queue')
	},
}

const nglSubmissionRequestQueue = useRequestQueue(
	'nglSubmissions',
	nglSubmissionsQueueConfig
)

// This getter exposes the ngl request queue to submission store
export const getQueue = () => {
	return nglSubmissionRequestQueue
}

// This method handles creating a NGL submission request and adding it to the queue
export const submitRequest = async (planHolder: Plan): Promise<void> => {
	if (!shouldSubmit(planHolder)) {
		return
	}

	const payloadData: NglSubmissionRequest = mapNglSubmissionPayload(planHolder)
	const nglRequestConfig: AxiosRequestConfig = {
		url: `${import.meta.env.VITE_PURCHASE_API_URL}/api/v1/insurance/ngl/submit`,
		data: payloadData,
	}

	try {
		await nglSubmissionRequestQueue.addRequest(planHolder.id, nglRequestConfig)
	} catch (error: any) {
		// only log to sentry, executeRequest takes care of updating appropriate status in the localForage
		captureException(error, {
			extra: {
				message: `Error submitting packet for NGL.`,
				response: error.response?.data,
			},
		})
	}
}

export const validateRequest = (planHolder: Plan): boolean => {
	switch (planHolder.payment.paymentType) {
		case PlanPaymentType.EFT:
			return validateEftPaymentDetails(
				planHolder.payment.paymentDetails as EFTPayment['paymentDetails']
			)
		case PlanPaymentType.CreditCard:
			return !!planHolder.payment.paymentDetails.token
		case PlanPaymentType.DirectBill:
			return !!planHolder.payment.paymentDetails.billingType
	}
}

const validateEftPaymentDetails = (
	paymentDetails: EFTPayment['paymentDetails']
): boolean => {
	// Ignore accountHolder for validation
	const { accountHolder, ...initialAccountWithoutAccountHolder } =
		paymentDetails.initialAccount

	const validInitialAccount = Object.values(
		initialAccountWithoutAccountHolder
	).every((value) => value)

	if (paymentDetails.useDifferentOngoingAccount) {
		const { accountHolder, ...ongoingAccountWithoutAccountHolder } =
			paymentDetails.ongoingAccount

		const validOngoingAccount = Object.values(
			ongoingAccountWithoutAccountHolder
		).every((value) => value)

		return validInitialAccount && validOngoingAccount
	} else {
		return validInitialAccount
	}
}

const mapNglSubmissionPayload = (planHolder: Plan): NglSubmissionRequest => {
	const documentStore = useDocumentStore()
	const sessionStore = useSessionStore()
	const sessionDocumentsStore = useSessionDocumentsStore()
	const featureService = useFeatureService()

	const authStore = useAuthStore()
	const salesforceContactAfpId = authStore.getActiveSalesContactId()

	const signatureServiceEnabled = featureService.isEnabled(
		Feature.RemoteSignature
	)

	let sessionId = sessionStore.activeSession?.id

	if (!sessionId) {
		const session = sessionStore.getSessionFromPlanHolderId(planHolder.id)
		sessionId = session?.id
	}

	const data = {
		sessionId: sessionId!, // in the very odd case this is missing we want to force the error to the request queue so it can retry later
		planId: planHolder.id,
		document: new File(
			[documentStore.getPlanHolderMergedDocument(planHolder)!.blob],
			`${planHolder.firstName}-${planHolder.lastName}-${DateTime.now().toFormat('MM-dd-yyyy')}`,
			{ type: 'application/pdf' }
		),
		correlationId: faker.string.uuid(),
		crmLeadId: planHolder.leadId,
		crmAppointmentId: sessionStore.activeSession?.appointmentId,
		planholderName: `${planHolder.firstName} ${planHolder.lastName}`,
		salesforceContactAfpId,
	}

	let signatureData = {}

	// if signature features is on
	if (signatureServiceEnabled) {
		const planId = planHolder.id
		if (sessionDocumentsStore.shouldSendSignaturePayload(planId)) {
			// grab the flattened missing signatures as signatures array
			signatureData = {
				signaturesRequired: 1,
				signers: mapSignerPayload(
					sessionDocumentsStore.selectedSigners[planId] as SignerInfo
				),
				signatures: mapSignaturePayload(
					sessionDocumentsStore.missingSignatures[planId]
				),
			}
			signatureData = JSON.parse(JSON.stringify(signatureData))
		}
	}

	return { ...data, ...signatureData }
}

const mapSignerPayload = (signerData: SignerInfo) => {
	// Currently we only send signature request for FuneralDirector,
	// will update this once we add more signer role in the future
	const { id, ...signer } = signerData
	return { FuneralDirector: signer }
}

const mapSignaturePayload = (
	missingSignatureBySigner: MissingSignaturesBySigner[]
) => {
	let payload: Record<any, MissingSignature[]> = {}
	missingSignatureBySigner.forEach(
		(signaturePairs) =>
			(payload[signaturePairs.signer] = signaturePairs.signatureFields)
	)
	return payload
}

const shouldSubmit = (plan: Plan): boolean => {
	return plan.completedSteps.includes(DocumentType.StatementOfGoodsAndServices)
}

export const nglService: SubmissionService = {
	getQueue,
	submitRequest,
	validateRequest,
	serviceName: 'NGL',
	storeName: 'nglSubmissions',
	shouldSubmit,
	...useRetryMethods(nglSubmissionRequestQueue, localNglSubmissions, 'NGL'),
}
